import { useSelector, useDispatch } from "react-redux";
import { useEffect } from "react";
import { IStore } from "@/redux/defaultStore";
import { useGetUserProfile } from "@/hooks/useGetUserProfile";
import { useGetUserLevel } from "@/hooks/useGetUserLevel";
import { useGetTransactionsPaginated } from "@/hooks/useGetTransactionsPaginated";
import { useGetOverwolfSummary } from "@/hooks/useGetOverwolfSummary";
import { useGetActiveChallenges } from "@/hooks/challenges/useGetActiveChallenges";
import { useGetUserLeaderboardRank } from "@/hooks/useGetUserLeaderboardRank";
import useGetUserOverwolfXP from "@/hooks/useGetUserOverwolfXP";
import { GetOverwolfXpSubjectEnum } from "@devour/client";
import { updateAccountLevel, updateCurrentUser } from "@/redux/meta/metaActions";

/**
 * Consolidated data hook for the Play page
 * Handles all data fetching and state management in one place
 */
export const usePlayPageData = () => {
    const dispatch = useDispatch();
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const currentUser = useSelector((store: IStore) => store.metaStore.currentUser);
    const accountLevel = useSelector((store: IStore) => store.metaStore.accountLevel);

    // Core user data with error handling
    const {
        data: userProfile,
        isLoading: isLoadingProfile,
        error: profileError,
    } = useGetUserProfile(fullToken);

    /*
     * We still need useGetUserLevel for proper XP calculations
     * But now it's cached for 1 minute instead of zero cache
     */
    const {
        data: userLevelData,
        isLoading: isLoadingLevel,
        error: levelError,
    } = useGetUserLevel(fullToken, currentUser?.user?.id);

    const {
        data: transactionsData,
        isLoading: isLoadingTransactions,
        error: transactionsError,
    } = useGetTransactionsPaginated(fullToken, currentUser?.user?.id);

    const {
        data: overwolfSummary,
        isLoading: isLoadingSummary,
        error: summaryError,
    } = useGetOverwolfSummary();

    // Get leaderboard rank
    const {
        data: leaderboardRankData,
        isLoading: isLoadingLeaderboard,
        error: leaderboardError,
    } = useGetUserLeaderboardRank(fullToken);

    const {
        data: challengesData,
        isLoading: isLoadingChallenges,
        error: challengesError,
    } = useGetActiveChallenges({ limit: 5 });

    const {
        data: timeRewardsData,
        isLoading: isLoadingTimeRewards,
        error: timeRewardsError,
    } = useGetUserOverwolfXP(
        fullToken,
        GetOverwolfXpSubjectEnum.TIMEREWARD,
        undefined,
    );

    // Update account level when data is fetched
    useEffect(() => {
        if (userLevelData) {
            dispatch(updateAccountLevel(userLevelData));
        }
    }, [userLevelData, dispatch]);

    // Update current user when profile is fetched
    useEffect(() => {
        if (userProfile) {
            dispatch(updateCurrentUser(userProfile));
        }
    }, [userProfile, dispatch]);

    // Calculate XP percentages
    const experienceGained = accountLevel?.experienceGainedAtThisLevel || 0;
    const experienceRequired = accountLevel?.experienceRequiredForNextLevel || 1;
    const totalLevelExperience = experienceGained + experienceRequired;
    const xpPercentage = experienceGained / totalLevelExperience * 100;

    // Format fuel balance
    const fuelBalance = transactionsData
        ? Intl.NumberFormat("en-us", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(transactionsData.balanceInUsd)
        : "0.00";

    // Aggregate loading state
    const isLoading = isLoadingProfile || isLoadingLevel || isLoadingTransactions ||
                    isLoadingSummary || isLoadingLeaderboard || isLoadingChallenges ||
                    isLoadingTimeRewards;

    // Aggregate error state
    const hasError = !!(profileError || levelError || transactionsError || summaryError ||
                     leaderboardError || challengesError || timeRewardsError);
    const errors = {
        profile: profileError,
        level: levelError,
        transactions: transactionsError,
        summary: summaryError,
        leaderboard: leaderboardError,
        challenges: challengesError,
        timeRewards: timeRewardsError,
    };

    // Extract leaderboard rank
    const leaderboardRank = leaderboardRankData?.rank || 0;
    const shouldShowUserRank = (userLevelData?.level || 0) > 1;

    return {
    // User data
        fullToken,
        currentUser,
        userProfile,
        accountLevel,
        overwolfSummary,
        userLevelData,
        leaderboardRankData,
        challengesData,
        timeRewardsData,

        // Formatted data
        fuelBalance,
        xpPercentage,
        experienceGained,
        experienceRequired,
        leaderboardRank,
        shouldShowUserRank,

        // Loading states
        isLoading,
        isLoadingProfile,
        isLoadingLevel,
        isLoadingTransactions,
        isLoadingSummary,
        isLoadingLeaderboard,
        isLoadingChallenges,
        isLoadingTimeRewards,

        // Error states
        hasError,
        errors,
    };
};

/**
 * Hook for managing Play page UI state
 * Separated from data fetching for better organization
 */
export const usePlayPageState = () => {
    // Get saved preferences from localStorage with error handling
    let savedTab = "games";
    let savedGameId: string | null = null;

    try {
        savedTab = localStorage.getItem("play-active-tab") || "games";
        savedGameId = localStorage.getItem("play-selected-game");
    } catch (error) {
        console.warn("Failed to read from localStorage:", error);
    }

    return {
        savedTab,
        savedGameId,
    };
};